import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { GuiButton } from "../../components/global-ui/gui-button";
import actions from "../../actions/actions";
import { useOnFirstLoad } from "../../utilities/hooks";
import AddPaymentModal from "./AddPaymentModal";
import { GuiSelect } from "../../components/global-ui/gui-select";
import { Modal } from "../../components/modal";
import navigate from "../../utilities/navigation";
import { USD_CURRENCY_FORMATTER } from "../../utilities/constants";

const wholeFormatter = new Intl.NumberFormat("en-US", {
  maximumFractionDigits: 0,
});

function pick(source, camelKey, pascalKey, fallback) {
  return source?.[camelKey] ?? source?.[pascalKey] ?? fallback;
}

function fmtCurrency(value) {
  const n = Number(value);
  return Number.isFinite(n) ? USD_CURRENCY_FORMATTER.format(n) : "$0.00";
}

function fmtWhole(value) {
  const n = Number(value);
  return Number.isFinite(n) ? wholeFormatter.format(n) : "0";
}

// Clients can only self-serve into a Monthly plan. Annual/quarterly clients must
// contact support to change their plan, so these cycles are never selectable.
const HIDDEN_BILLING_CYCLES = ["annually", "annual", "quarterly"];

function isNonMonthlyCycle(cycle) {
  return HIDDEN_BILLING_CYCLES.includes(String(cycle).toLowerCase());
}

// The billing cycle the client is allowed to switch to. Annual/quarterly clients
// are forced down to Monthly; everyone else keeps their existing/allowed cycle.
function resolveWorkingBillingCycle(data) {
  const current = data?.selectedBillingCycle || data?.currentBillingCycle || "";
  if (isNonMonthlyCycle(current)) return "Monthly";
  return (
    data?.selectedBillingCycle ||
    data?.currentBillingCycle ||
    data?.allowedBillingCycles?.[0] ||
    "Monthly"
  );
}

function ChangePlanModal({ onClose, onSaved, planPageData }) {
  const [activePlanPageData, setActivePlanPageData] = useState(planPageData);
  const initialBillingCycle = resolveWorkingBillingCycle(activePlanPageData);

  const [billingCycle, setBillingCycle] = useState(initialBillingCycle);
  const [planId, setPlanId] = useState(
    activePlanPageData?.selectedPlanId ?? null,
  );
  const [plans, setPlans] = useState(activePlanPageData?.plans || []);
  const [preview, setPreview] = useState(null);
  const [loadingPlans, setLoadingPlans] = useState(false);
  const [loadingPage, setLoadingPage] = useState(!activePlanPageData);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(null);

  const currentPlanName =
    activePlanPageData?.currentPlanDisplay ||
    activePlanPageData?.currentPlanName ||
    "—";
  const currentBillingCycle =
    activePlanPageData?.currentBillingCycle ||
    activePlanPageData?.billingCycle ||
    "—";
  const isAnnualCustomer = isNonMonthlyCycle(
    activePlanPageData?.currentBillingCycle ||
      activePlanPageData?.billingCycle ||
      "",
  );
  const accountStatus = pick(
    activePlanPageData,
    "accountStatus",
    "AccountStatus",
    "",
  );
  const isFreeTrialCustomer = pick(
    activePlanPageData,
    "isFreeTrialCustomer",
    "IsFreeTrialCustomer",
    false,
  );
  const trialDaysRemaining = pick(
    activePlanPageData,
    "trialDaysRemaining",
    "TrialDaysRemaining",
    0,
  );
  const billingCycleOptions = (activePlanPageData?.allowedBillingCycles || [])
    .filter((cycle) => !isNonMonthlyCycle(cycle))
    .map((cycle) => ({
      value: cycle,
      label: cycle,
    }));
  const planOptions = plans.map((plan) => ({
    value: plan.id,
    label: plan.display || plan.name,
  }));
  const selectedPlan =
    planOptions.find((option) => option.value === planId) || null;
  const currentPlanId = pick(
    activePlanPageData,
    "currentPlanId",
    "CurrentPlanId",
    null,
  );
  const currentPlanCredits =
    plans.find((plan) => plan.id === currentPlanId)?.credits ?? null;
  const currentPlanLabel =
    currentPlanCredits > 0 && !/credit/i.test(currentPlanName)
      ? `${currentPlanName} (${fmtWhole(currentPlanCredits)} credits)`
      : currentPlanName;
  const newPlanLabel =
    billingCycle === "Bulk" ? "Bulk" : selectedPlan?.label || "—";
  const previewErrors = preview?.validationErrors || [];
  const canSave = Boolean(preview?.canSave) && !saving && !loadingPage;

  useEffect(() => {
    let cancelled = false;

    async function loadPage() {
      setLoadError(null);
      if (planPageData) {
        setActivePlanPageData(planPageData);
        setBillingCycle(resolveWorkingBillingCycle(planPageData));
        setPlanId(planPageData.selectedPlanId ?? null);
        setPlans(planPageData.plans || []);
        setLoadingPage(false);
        return;
      }

      const result = await actions.manageMessagePlan.getPage();
      if (cancelled) return;

      if (result?.success !== false) {
        setActivePlanPageData(result);
        setBillingCycle(resolveWorkingBillingCycle(result));
        setPlanId(result.selectedPlanId ?? null);
        setPlans(result.plans || []);
      } else {
        setLoadError(result?.errorMessage || "Unable to load plan details.");
      }

      setLoadingPage(false);
    }

    loadPage();
    return () => {
      cancelled = true;
    };
  }, [planPageData]);

  useEffect(() => {
    let cancelled = false;

    async function loadPlans() {
      if (billingCycle === "Bulk") {
        setPlans([]);
        setPlanId(null);
        setLoadingPlans(false);
        return;
      }

      setLoadingPlans(true);
      const result = await actions.manageMessagePlan.getPlans(billingCycle);
      if (cancelled) return;

      if (result?.success !== false) {
        const nextPlans = result.plans || [];
        setPlans(nextPlans);
        setPlanId(result.selectedPlanId ?? nextPlans[0]?.id ?? null);
      } else {
        setLoadError(result?.errorMessage || "Unable to load plans.");
      }

      setLoadingPlans(false);
    }

    loadPlans();
    return () => {
      cancelled = true;
    };
  }, [billingCycle]);

  useEffect(() => {
    let cancelled = false;

    async function loadPreview() {
      if (!billingCycle) return;

      setLoadingPreview(true);
      const result = await actions.manageMessagePlan.preview({
        billingCycle,
        planId: billingCycle === "Bulk" ? null : planId,
      });

      if (!cancelled) {
        if (result?.success !== false) {
          setPreview(result);
        } else {
          setLoadError(
            result?.errorMessage || "Unable to validate the selected plan.",
          );
        }
        setLoadingPreview(false);
      }
    }

    loadPreview();
    return () => {
      cancelled = true;
    };
  }, [billingCycle, planId]);

  function handleBillingCycleChange(_, option) {
    setBillingCycle(option?.value ?? option ?? "");
  }

  function handlePlanChange(_, option) {
    setPlanId(option?.value ?? null);
  }

  async function handleConfirm() {
    if (!canSave) return;

    setSaving(true);
    try {
      const result = await actions.manageMessagePlan.change({
        billingCycle,
        planId: billingCycle === "Bulk" ? null : planId,
      });

      if (result?.success !== false) {
        onSaved?.(result);
        if (result?.redirectUrl) {
          navigate(result?.redirectUrl);
          return;
        }
        onClose();
      }
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      className="change-plan-modal plain-modal"
      onClose={onClose}
      showModal
    >
      <div className="change-plan-modal__header">
        <span className="change-plan-modal__title">Change Plan</span>
        <button
          className="change-plan-modal__close-btn"
          onClick={onClose}
          aria-label="Close"
        >
          <i className="fas fa-times" />
        </button>
      </div>

      <div className="change-plan-modal__body">
        <div className="change-plan-modal__notice">
          <i className="fas fa-info-circle" />
          <span>
            <>
              Your new plan will take effect on your scheduled billing date. If
              you need to change the billing date, please contact{" "}
              <a href="mailto:billing@highgroundsolutions.com">
                billing@highgroundsolutions.com
              </a>
            </>
          </span>
        </div>

        {accountStatus && (
          <div className="change-plan-modal__status">
            <span className="change-plan-modal__status-label">
              Current account status
            </span>
            <span className="change-plan-modal__status-value">
              <i className="fas fa-circle" />
              {accountStatus}
              {isFreeTrialCustomer && trialDaysRemaining > 0
                ? ` · ${trialDaysRemaining} day${
                    trialDaysRemaining === 1 ? "" : "s"
                  } left`
                : ""}
            </span>
          </div>
        )}

        {loadError && (
          <div className="change-plan-modal__error">
            <i className="fas fa-exclamation-circle" />
            <span>{loadError}</span>
          </div>
        )}
        {(loadingPage || loadingPreview) && (
          <div className="change-plan-modal__loading">
            <i className="fas fa-circle-notch fa-spin" />
            <span>Loading plan details...</span>
          </div>
        )}

        <div className="change-plan-modal__columns">
          <div className="change-plan-modal__card change-plan-modal__card--current">
            <span className="change-plan-modal__card-title">Current Plan</span>
            <div className="change-plan-modal__field">
              <span className="change-plan-modal__field-label">
                Billing cycle
              </span>
              <span className="change-plan-modal__field-value">
                {currentBillingCycle}
              </span>
            </div>
            <div className="change-plan-modal__field">
              <span className="change-plan-modal__field-label">
                Selected plan
              </span>
              <span className="change-plan-modal__field-value">
                {currentPlanLabel}
              </span>
            </div>
          </div>

          <div className="change-plan-modal__arrow" aria-hidden="true">
            <i className="fas fa-arrow-right" />
          </div>

          <div className="change-plan-modal__card change-plan-modal__card--new">
            <span className="change-plan-modal__card-title">New Plan</span>
            <div className="change-plan-modal__field">
              <span className="change-plan-modal__field-label">
                Billing cycle
              </span>
              <span className="change-plan-modal__field-value">
                {billingCycle || "—"}
              </span>
            </div>
            <div className="change-plan-modal__field">
              <span className="change-plan-modal__field-label">
                Selected plan
              </span>
              <span className="change-plan-modal__field-value change-plan-modal__field-value--highlight">
                {newPlanLabel}
              </span>
            </div>
          </div>
        </div>

        <div className="change-plan-modal__controls">
          <div className="change-plan-modal__controls-row">
            <div className="change-plan-modal__field">
              <span className="change-plan-modal__field-label">
                Choose billing cycle
              </span>
              <GuiSelect
                name="billingCycle"
                options={billingCycleOptions}
                value={billingCycle}
                onChange={handleBillingCycleChange}
                disabled={isAnnualCustomer}
                menuPlacement="top"
                inModal
              />
            </div>
            <div className="change-plan-modal__field">
              <span className="change-plan-modal__field-label">
                Choose a plan
              </span>
              <GuiSelect
                name="plan"
                options={planOptions}
                value={selectedPlan}
                onChange={handlePlanChange}
                placeholder={
                  loadingPlans ? "Loading plans..." : "Select a plan"
                }
                disabled={
                  loadingPlans || billingCycle === "Bulk" || isAnnualCustomer
                }
                menuPlacement="top"
                inModal
              />
            </div>
          </div>
          {isAnnualCustomer && (
            <div className="change-plan-modal__annual-note">
              <i className="fas fa-lightbulb" />
              <span>
                Considering upgrading your annual plan? Feel free to reach out
                to us at{" "}
                <a href="mailto:support@highgroundsolutions.com">
                  support@highgroundsolutions.com
                </a>
              </span>
            </div>
          )}
        </div>

        {!isAnnualCustomer && previewErrors.length > 0 && (
          <div className="change-plan-modal__errors">
            {previewErrors.map((error, index) => (
              <div key={index} className="change-plan-modal__error">
                {error}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="change-plan-modal__footer">
        <GuiButton
          className="btn-outline change-plan-modal__cancel-btn"
          onClick={onClose}
        >
          Cancel
        </GuiButton>
        {!isAnnualCustomer && (
          <GuiButton
            className="change-plan-modal__confirm-btn"
            disabled={!canSave}
            onClick={handleConfirm}
          >
            {saving ? "Saving..." : "Save Plan"}
          </GuiButton>
        )}
      </div>
    </Modal>
  );
}

function StatBox({ label, value, subtitle, wide }) {
  return (
    <div
      className={`plan-details__stat-box${wide ? " plan-details__stat-box--wide" : ""}`}
    >
      <span className="plan-details__stat-label">{label}</span>
      <span className="plan-details__stat-value">{value}</span>
      {subtitle && <span className="plan-details__stat-sub">{subtitle}</span>}
    </div>
  );
}

function UsageBar({ label, used, total }) {
  const remaining = Math.max(0, total - used);
  const pct = total > 0 ? Math.min((used / total) * 100, 100) : 0;

  return (
    <div className="usage-balance__item">
      <div className="usage-balance__header">
        <span className="usage-balance__label">{label}</span>
        <span className="usage-balance__numbers">
          <strong>{fmtWhole(used)}</strong> / {fmtWhole(total)}
        </span>
      </div>
      <div className="usage-balance__bar">
        <div className="usage-balance__bar-fill" style={{ width: `${pct}%` }} />
      </div>
      <span className="usage-balance__remaining">
        {fmtWhole(remaining)} remaining
      </span>
    </div>
  );
}

export default function AccountPlanUsage({ planUsageData, onSave }) {
  const [showChangePlan, setShowChangePlan] = useState(false);
  const [showPlanChangeNotice, setShowPlanChangeNotice] = useState(false);
  const [planPageData, setPlanPageData] = useState(null);
  const user = useSelector((state) => state.user);
  const userName = user ? `${user.userName}` : "";
  const callPlanName = pick(planUsageData, "callPlanName", "CallPlanName", "—");
  const callPlanCost = pick(planUsageData, "callPlanCost", "CallPlanCost", 0);
  const overageRate = pick(planUsageData, "overageRate", "OverageRate", 0);
  const emailOverageRate = pick(
    planUsageData,
    "emailOverageRate",
    "EmailOverageRate",
    0,
  );
  const purchasedMessages = pick(
    planUsageData,
    "purchasedMessages",
    "PurchasedMessages",
    0,
  );
  const purchasedEmails = pick(
    planUsageData,
    "purchasedEmails",
    "PurchasedEmails",
    0,
  );
  const voiceSmsBalance = pick(
    planUsageData,
    "voiceSmsBalance",
    "VoiceSmsBalance",
    0,
  );
  const emailBalance = pick(planUsageData, "emailBalance", "EmailBalance", 0);

  const [paymentMethods, setPaymentMethods] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingProfile, setEditingProfile] = useState(null);

  function loadPaymentMethods() {
    actions.payment.getPaymentMethods().then((result) => {
      if (result?.success) setPaymentMethods(result.profiles || []);
    });
  }

  function loadPlanPageData() {
    actions.manageMessagePlan.getPage().then((result) => {
      if (result?.success) setPlanPageData(result);
    });
  }

  useOnFirstLoad(() => {
    loadPaymentMethods();
    loadPlanPageData();
  });

  function handlePaymentSaved(newProfile) {
    setPaymentMethods((prev) => {
      const existingIndex = prev.findIndex(
        (p) => p.paymentProfileId === newProfile.paymentProfileId,
      );

      if (existingIndex >= 0) {
        return prev.map((p) =>
          p.paymentProfileId === newProfile.paymentProfileId
            ? { ...p, ...newProfile }
            : p,
        );
      }

      if (newProfile.isDefault) {
        return [newProfile, ...prev.map((p) => ({ ...p, isDefault: false }))];
      }
      return [...prev, newProfile];
    });
    setShowAddModal(false);
    setEditingProfile(null);
    loadPaymentMethods();
  }

  function handleOpenEdit(profile) {
    setEditingProfile(profile);
    setShowAddModal(true);
  }

  function handlePlanSaved() {
    setShowChangePlan(false);
    setShowPlanChangeNotice(true);
    loadPlanPageData();
    onSave?.();
  }

  return (
    <div className="account-plan-usage">
      {/* ── Plan Details ── */}
      <div className="plan-details">
        <h3 className="plan-details__title">Plan Details</h3>

        {showPlanChangeNotice && (
          <div
            className="plan-details__info-banner plan-details__info-banner--plan-change"
            role="status"
          >
            <i className="fas fa-info-circle" />
            <span>
              Your new plan will take effect on your scheduled billing date. If
              you need to change the billing date, please contact{" "}
              <a href="mailto:billing@highgroundsolutions.com">
                billing@highgroundsolutions.com
              </a>
            </span>
          </div>
        )}

        <div className="plan-details__info-banner">
          <i className="fas fa-info-circle" />
          Voice, text &amp; email messages each count as 1 message. Voice
          messages are up to 2 minutes.
        </div>

        <div className="plan-details__stats-grid">
          <StatBox
            label="Call Plan"
            value={callPlanName}
            subtitle="billed annually"
          />
          <StatBox
            label="Cost"
            value={fmtCurrency(callPlanCost)}
            subtitle="per year"
          />
          <StatBox
            label="Overage Rate"
            value={fmtCurrency(overageRate)}
            subtitle="per message"
          />
          <StatBox
            label="Email overage rate"
            value={fmtCurrency(emailOverageRate)}
            subtitle="per email"
          />
          <StatBox
            label="Purchased messages"
            value={fmtWhole(purchasedMessages)}
            wide
          />
          <StatBox
            label="Purchased Emails"
            value={fmtWhole(purchasedEmails)}
            wide
          />
        </div>

        <GuiButton
          className="btn-outline plan-details__change-plan-btn"
          onClick={() => setShowChangePlan(true)}
        >
          Change Plan
        </GuiButton>

        {showChangePlan && (
          <ChangePlanModal
            onClose={() => setShowChangePlan(false)}
            onSaved={handlePlanSaved}
            planPageData={planPageData}
          />
        )}
      </div>

      {/* ── Usage Balance ── */}
      <div className="usage-balance">
        <h3 className="usage-balance__title">Usage Balance</h3>
        <div className="usage-balance__grid">
          <UsageBar
            label="Voice/SMS balance"
            used={voiceSmsBalance}
            total={purchasedMessages}
          />
          <UsageBar
            label="Email balance"
            used={emailBalance}
            total={purchasedEmails}
          />
        </div>
      </div>

      {/* ── Payment Methods ── */}
      <hr className="plan-usage-divider" />
      <div className="payment-methods">
        <div className="payment-methods__header">
          <h3 className="payment-methods__title">Payment Methods</h3>
          <button
            className="payment-methods__add-link"
            onClick={() => setShowAddModal(true)}
          >
            + Add new payment
          </button>
        </div>

        {paymentMethods.length === 0 ? (
          <p className="payment-methods__empty">
            No payment methods added yet.
          </p>
        ) : (
          <table className="payment-methods__table">
            <thead>
              <tr>
                <th>Profile Name</th>
                <th>Card Type</th>
                <th>Card Number</th>
                <th>Expires</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {paymentMethods.map((p) => (
                <tr key={p.paymentProfileId}>
                  <td>
                    {p.profileName}
                    {p.isDefault && (
                      <span className="payment-methods__default-badge">
                        Default
                      </span>
                    )}
                  </td>
                  <td>{p.cardType ?? "—"}</td>
                  <td>{p.lastFour ? `••••${p.lastFour}` : "—"}</td>
                  <td>{p.expiryDisplay ?? "—"}</td>
                  <td>
                    <button
                      className="payment-methods__edit-btn"
                      onClick={() => handleOpenEdit(p)}
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showAddModal && (
        <AddPaymentModal
          paymentMethod={editingProfile}
          userName={userName}
          onClose={() => {
            setShowAddModal(false);
            setEditingProfile(null);
          }}
          onSaved={handlePaymentSaved}
        />
      )}
    </div>
  );
}
